import React, { useCallback, useEffect } from 'react'
import {
  ButtonSizeEnum,
  ButtonTypeEnum,
  CommonButton,
  CommonButtonProps,
} from 'src/editor/shared/ui-lib/buttons'
import { twMerge } from 'tailwind-merge'
import { ModalWindowSizeEnum } from '../types'

interface ButtonProps extends Omit<CommonButtonProps, 'onClick'> {
  onClick?: () => void
}
export interface ModalWindowFooterProps {
  size?: ModalWindowSizeEnum
  onClose?: () => void
  cancelButtonProps?: ButtonProps
  confirmButtonProps?: ButtonProps
  className?: string
  hideHorizontalBorder?: boolean
  confirmOnEnter?: boolean
}
export const ModalWindowFooter = ({
  size = ModalWindowSizeEnum.Medium,
  onClose,
  cancelButtonProps,
  confirmButtonProps,
  className,
  hideHorizontalBorder,
  confirmOnEnter,
}: ModalWindowFooterProps) => {
  const onConfirm = useCallback(() => {
    if (confirmButtonProps?.disabled || confirmButtonProps?.isLoading) {
      return
    }
    confirmButtonProps?.onClick?.()
  }, [confirmButtonProps])

  const onCancel = useCallback(() => {
    cancelButtonProps?.onClick?.()
    onClose?.()
  }, [cancelButtonProps, onClose])

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && confirmOnEnter) {
        e.preventDefault()
        onConfirm()
      }
    }
    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [confirmOnEnter, onConfirm])

  return cancelButtonProps || confirmButtonProps ? (
    <div
      className={twMerge(
        'flex w-full items-center justify-start gap-3',
        !hideHorizontalBorder && 'border-border200 border-t',
        size === ModalWindowSizeEnum.Small && 'px-6 py-3',
        size === ModalWindowSizeEnum.Medium && 'px-8 pt-5 pb-6',
        className,
      )}
    >
      {cancelButtonProps && (
        <CommonButton
          {...cancelButtonProps}
          onClick={onCancel}
          type={cancelButtonProps?.type ?? ButtonTypeEnum.Tertiary}
          size={cancelButtonProps?.size ?? ButtonSizeEnum.Medium}
        />
      )}
      {confirmButtonProps && (
        <CommonButton
          {...confirmButtonProps}
          onClick={onConfirm}
          type={confirmButtonProps?.type ?? ButtonTypeEnum.Primary}
          size={confirmButtonProps?.size ?? ButtonSizeEnum.Medium}
        />
      )}
    </div>
  ) : null
}
